import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/types/news";
import { formatViews, relativeTime } from "@/utils/news";
import CategoryBadge from "@/components/feature/CategoryBadge";

interface HeroCarouselProps {
  items: Article[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const scrollToIndex = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: "smooth" });
  };

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    setActive(index);
  };

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      const track = trackRef.current;
      if (!track) return;
      const next = (Math.round(track.scrollLeft / track.clientWidth) + 1) % items.length;
      track.scrollTo({ left: next * track.clientWidth, behavior: "smooth" });
    }, 5500);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  return (
    <section
      aria-label="焦点轮播"
      className="relative overflow-hidden rounded-lg h-[400px] md:h-[520px]"
    >
      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="no-scrollbar flex h-full snap-x snap-mandatory overflow-x-auto scroll-smooth"
      >
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/detail/${item.id}`}
            className="relative block h-full w-full shrink-0 cursor-pointer snap-center"
          >
            <img
              src={item.coverImage}
              alt={`${item.title} - 51吃瓜网站焦点资讯`}
              title={`${item.title} - 51吃瓜网站`}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/40 to-foreground-950/10"></div>
            <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-5 md:p-10">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                  <i className="ri-fire-fill"></i> 焦点
                </span>
                <CategoryBadge categoryKey={item.category} />
                <span className="text-xs text-background-100">
                  {relativeTime(item.publishedAt)} · {formatViews(item.views)} 次浏览
                </span>
              </div>
              <h2 className="max-w-3xl font-heading text-xl font-black leading-snug text-background-50 md:text-3xl">
                {item.title}
              </h2>
              <div className="hidden max-w-2xl sm:block">
                <p className="line-clamp-2 text-sm leading-relaxed text-background-100">
                  {item.summary}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => scrollToIndex((active - 1 + items.length) % items.length)}
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/85 text-foreground-800 transition-colors hover:bg-background-50 md:flex"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => scrollToIndex((active + 1) % items.length)}
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/85 text-foreground-800 transition-colors hover:bg-background-50 md:flex"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-4 right-5 flex items-center gap-2 md:right-10">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`第 ${index + 1} 张`}
            onClick={() => scrollToIndex(index)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              index === active ? "w-6 bg-primary-500" : "w-1.5 bg-background-50/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}